import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useTheme } from '@mui/material/styles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import expenseRequestService from '../services/expenseRequestService';
import projectService from '../services/projectService';
import { downloadBlobFile, openBlobFile } from '../services/nativeFileService';
import { useLocation, useNavigate } from 'react-router-dom';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'needs_invoice', label: 'Needs Invoice' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'paid', label: 'Paid' }
];

const CATEGORY_OPTIONS = [
  'Travel',
  'Meals',
  'Hotel',
  'Fuel',
  'Tools',
  'Supplies',
  'Services',
  'Shipping',
  'Other'
];

const CURRENCY_OPTIONS = ['MXN', 'USD'];

const buildEmptyForm = () => ({
  title: '',
  category: 'Travel',
  vendor_name: '',
  invoice_number: '',
  expense_date: new Date().toISOString().split('T')[0],
  amount: '',
  currency: 'MXN',
  project_id: '',
  description: '',
  status: 'submitted',
  review_notes: ''
});

const normalizeResponseRequests = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.requests)) return payload.requests;
  return [];
};

const formatMoney = (amount, currency) => {
  const value = Number.parseFloat(amount || 0) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'MXN'
  }).format(value);
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const getStatusChipProps = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'paid':
      return { label: 'Paid', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' };
    case 'approved':
      return { label: 'Approved', color: '#38bdf8', bg: 'rgba(56,189,248,0.16)' };
    case 'under_review':
      return { label: 'Under Review', color: '#f59e0b', bg: 'rgba(245,158,11,0.16)' };
    case 'needs_invoice':
      return { label: 'Needs Invoice', color: '#f97316', bg: 'rgba(249,115,22,0.16)' };
    case 'rejected':
      return { label: 'Rejected', color: '#ef4444', bg: 'rgba(239,68,68,0.14)' };
    default:
      return { label: 'Submitted', color: '#60a5fa', bg: 'rgba(96,165,250,0.14)' };
  }
};

const openBlobResponse = (response, { download = false, fallbackName = 'file' } = {}) => {
  const blob = response?.data;
  if (!blob) return;
  const options = {
    fileName: fallbackName,
    mimeType: response.headers?.['content-type'] || blob.type
  };
  return download ? downloadBlobFile(blob, options) : openBlobFile(blob, options);
};

function ExpenseTicketDialog({
  open,
  onClose,
  onSubmit,
  saving,
  form,
  setForm,
  selectedFiles,
  setSelectedFiles,
  projects,
  canApprove,
  editMode,
  existingFiles = [],
  onOpenExistingFile,
  requestId,
  comments = [],
  commentsLoading = false,
  commentDraft = '',
  onCommentDraftChange,
  onAddComment,
  onDeleteComment,
  commentSubmitting = false,
  currentUserId
}) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setSelectedFiles((prev) => [...prev, ...files]);
    event.target.value = '';
  };

  const removeSelectedFile = (indexToRemove) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha(accent, 0.24),
          backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography component="span" variant="h6" sx={{ fontWeight: 800, display: 'block' }}>
          {editMode ? 'Update Expense Ticket' : 'New Expense Ticket'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          It is processed for payment only when it includes an invoice or verifiable supporting evidence.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Concept / Title"
              value={form.title}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Category"
              value={form.category}
              onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}
              size="small"
            >
              {CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Vendor / Supplier"
              value={form.vendor_name}
              onChange={(event) => setForm((prev) => ({ ...prev, vendor_name: event.target.value }))}
              size="small"
              placeholder="e.g. Hotel, airline, tool vendor"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Invoice / Folio Number"
              value={form.invoice_number}
              onChange={(event) => setForm((prev) => ({ ...prev, invoice_number: event.target.value }))}
              size="small"
              placeholder="Invoice, folio, or reference"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="date"
              label="Expense Date"
              value={form.expense_date}
              onChange={(event) => setForm((prev) => ({ ...prev, expense_date: event.target.value }))}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Amount"
              value={form.amount}
              onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))}
              size="small"
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Currency"
              value={form.currency}
              onChange={(event) => setForm((prev) => ({ ...prev, currency: event.target.value }))}
              size="small"
            >
              {CURRENCY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Project (Optional)"
              value={form.project_id}
              onChange={(event) => setForm((prev) => ({ ...prev, project_id: event.target.value }))}
              size="small"
            >
              <MenuItem value="">No project linked</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={String(project.id)}>
                  {project.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Description / Business Reason"
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              helperText="Explain the expense and why it should be reimbursed."
            />
          </Grid>

          {editMode && !canApprove && form.review_notes && <Grid item xs={12}><Alert severity="info"><Typography fontWeight={800}>Observaciones de revisión</Typography><Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: .5 }}>{form.review_notes}</Typography><Typography variant="body2" sx={{ mt: 1 }}>{form.status === 'needs_invoice' ? 'Completa la información en este mismo ticket y conserva su conversación.' : 'Consulta la conversación para ver el seguimiento de este ticket.'}</Typography></Alert></Grid>}

          {canApprove && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={form.status}
                  onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}
                  size="small"
                >
                  {STATUS_OPTIONS.filter((item) => item.value !== 'all').map((item) => (
                    <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Review Notes"
                  value={form.review_notes}
                  onChange={(event) => setForm((prev) => ({ ...prev, review_notes: event.target.value }))}
                  size="small"
                  placeholder="Approval comments, missing data, payment notes..."
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderStyle: 'dashed',
                borderColor: alpha(accent, 0.4),
                bgcolor: alpha(accent, 0.04)
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Invoice Evidence
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Adjunta PDF, imagen o soporte que compruebe el gasto.
                  </Typography>
                </Box>
                <Button component="label" variant="outlined" startIcon={<AttachFileIcon />}>
                  Add Files
                  <input hidden multiple type="file" onChange={handleFileChange} />
                </Button>
              </Stack>

              {existingFiles.length > 0 && (
                <Stack spacing={1} sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Existing evidence: {existingFiles.length} file(s)
                  </Typography>
                  {existingFiles.map((file) => (
                    <Box
                      key={file.id}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1,
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.background.default, 0.6)
                      }}
                    >
                      <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                        {file.original_name}
                      </Typography>
                      <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap' }} useFlexGap>
                        <Button size="small" onClick={() => onOpenExistingFile(requestId, file, false)} sx={{ flex: { xs: 1, sm: 'initial' } }}>
                          Open
                        </Button>
                        <Button size="small" onClick={() => onOpenExistingFile(requestId, file, true)} sx={{ flex: { xs: 1, sm: 'initial' } }}>
                          Download
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}

              {selectedFiles.length > 0 && (
                <Stack spacing={1} sx={{ mt: 1.5 }}>
                  {selectedFiles.map((file, index) => (
                    <Box
                      key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1,
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.background.default, 0.6)
                      }}
                    >
                      <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                        {file.name}
                      </Typography>
                      <Button color="error" size="small" onClick={() => removeSelectedFile(index)} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>

          {editMode && requestId && (
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2.2,
                  borderColor: alpha(accent, 0.24),
                  bgcolor: alpha(theme.palette.background.paper, 0.55)
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1.2 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Conversation / Follow-up
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Administrators and employees can record ticket updates here.
                    </Typography>
                  </Box>
                  <Chip size="small" label={`${comments.length} note(s)`} sx={{ alignSelf: 'flex-start' }} />
                </Stack>

                <Box
                  sx={{
                    maxHeight: 260,
                    overflowY: 'auto',
                    pr: 0.5,
                    mb: 1.5,
                    display: 'grid',
                    gap: 1
                  }}
                >
                  {commentsLoading ? (
                    <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : comments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No notes yet. Use this thread for clarifications, invoice requests and payment follow-up.
                    </Typography>
                  ) : comments.map((comment) => {
                    const isSystem = Number(comment.is_system) === 1;
                    const isOwnComment = Number(comment.user_id || 0) === Number(currentUserId || 0);
                    const canDeleteComment = !isSystem && (isOwnComment || canApprove);

                    return (
                      <Box
                        key={comment.id}
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: isSystem ? alpha(theme.palette.warning.main, 0.3) : alpha(theme.palette.divider, 0.45),
                          bgcolor: isSystem
                            ? alpha(theme.palette.warning.main, 0.08)
                            : (isOwnComment ? alpha(accent, 0.08) : alpha(theme.palette.background.default, 0.55))
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box>
                            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                {comment.author_name || 'User'}
                              </Typography>
                              {isSystem && <Chip size="small" label="System" color="warning" variant="outlined" />}
                              {!isSystem && comment.role && (
                                <Chip
                                  size="small"
                                  label={String(comment.role).replace(/^\w/, (letter) => letter.toUpperCase())}
                                  variant="outlined"
                                />
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {formatDateTime(comment.created_at)}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ mt: 0.8, whiteSpace: 'pre-wrap' }}>
                              {comment.comment}
                            </Typography>
                          </Box>
                          {canDeleteComment && (
                            <IconButton size="small" color="error" onClick={() => onDeleteComment(comment.id)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={5}
                    label="Add a note"
                    value={commentDraft}
                    onChange={(event) => onCommentDraftChange(event.target.value)}
                    placeholder="Ask for invoice corrections, confirm payment, or leave follow-up notes..."
                  />
                  <Button
                    variant="contained"
                    startIcon={commentSubmitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                    onClick={onAddComment}
                    disabled={commentSubmitting || !commentDraft.trim()}
                    sx={{ minWidth: 140, alignSelf: { xs: 'stretch', sm: 'center' } }}
                  >
                    Send
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <ReceiptLongIcon />}>
          {editMode ? 'Update Ticket' : 'Create Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ExpenseTicketsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const linkedRequestId = new URLSearchParams(location.search).get('requestId');
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canApprove = hasPermission('expenseRequests', 'approve');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    submitted: 0,
    needs_invoice: 0,
    under_review: 0,
    approved: 0,
    rejected: 0,
    paid: 0
  });
  const [filters, setFilters] = useState({
    scope: canApprove ? 'all' : 'mine',
    status: 'all',
    search: ''
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [form, setForm] = useState(buildEmptyForm());
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });
  const [openingFileKey, setOpeningFileKey] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(filters.search.trim()), 240);
    return () => window.clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    if (!canApprove && filters.scope !== 'mine') {
      setFilters((prev) => ({ ...prev, scope: 'mine' }));
    }
  }, [canApprove, filters.scope]);

  const loadProjects = useCallback(async () => {
    try {
      const response = await projectService.getProjects();
      setProjects(Array.isArray(response?.projects) ? response.projects : []);
    } catch (error) {
      setProjects([]);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await expenseRequestService.getRequests({
        scope: filters.scope,
        status: filters.status,
        search: debouncedSearch
      });
      setRequests(normalizeResponseRequests(response));
      setSummary(response?.summary || {
        total: 0,
        submitted: 0,
        needs_invoice: 0,
        under_review: 0,
        approved: 0,
        rejected: 0,
        paid: 0
      });
    } catch (error) {
      setNotice({
        open: true,
        severity: 'error',
        message: error.response?.data?.message || 'Could not load expense tickets.'
      });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters.scope, filters.status]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const loadComments = useCallback(async (requestId) => {
    if (!requestId) {
      setComments([]);
      return;
    }

    try {
      setCommentsLoading(true);
      const response = await expenseRequestService.getComments(requestId);
      setComments(Array.isArray(response?.comments) ? response.comments : []);
    } catch (error) {
      setComments([]);
      setNotice({
        open: true,
        severity: 'error',
        message: error.response?.data?.message || 'Could not load the conversation.'
      });
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  const openCreateDialog = () => {
    setEditingRequest(null);
    setForm(buildEmptyForm());
    setSelectedFiles([]);
    setComments([]);
    setCommentDraft('');
    setDialogOpen(true);
  };

  const openEditDialog = useCallback((request) => {
    setEditingRequest(request);
    setForm({
      title: request.title || '',
      category: request.category || 'Travel',
      vendor_name: request.vendor_name || '',
      invoice_number: request.invoice_number || '',
      expense_date: request.expense_date ? String(request.expense_date).slice(0, 10) : new Date().toISOString().split('T')[0],
      amount: request.amount || '',
      currency: request.currency || 'MXN',
      project_id: request.project_id ? String(request.project_id) : '',
      description: request.description || '',
      status: request.status || 'submitted',
      review_notes: request.review_notes || ''
    });
    setSelectedFiles([]);
    setCommentDraft('');
    setDialogOpen(true);
  }, []);

  useEffect(() => {
    if (!linkedRequestId) return undefined;
    let active = true;
    const consumeLink = () => {
      const params = new URLSearchParams(location.search); params.delete('requestId');
      navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
    };
    if (!/^\d+$/.test(linkedRequestId) || Number(linkedRequestId) <= 0) {
      setNotice({ open: true, severity: 'error', message: 'Identificador de ticket inválido.' }); consumeLink(); return undefined;
    }
    expenseRequestService.getRequest(Number(linkedRequestId)).then((response) => {
      if (active) openEditDialog(response.request);
    }).catch((failure) => {
      if (active) setNotice({ open: true, severity: 'error', message: failure.response?.data?.message || 'No se pudo abrir el ticket.' });
    }).finally(() => { if (active) consumeLink(); });
    return () => { active = false; };
  }, [linkedRequestId, location.pathname, location.search, navigate, openEditDialog, user?.id]);

  useEffect(() => {
    if (!dialogOpen || !editingRequest?.id) {
      setComments([]);
      setCommentDraft('');
      return;
    }
    loadComments(editingRequest.id);
  }, [dialogOpen, editingRequest?.id, loadComments]);

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingRequest(null);
    setForm(buildEmptyForm());
    setSelectedFiles([]);
    setComments([]);
    setCommentDraft('');
  };

  const submitForm = async () => {
    const hasExistingFiles = Array.isArray(editingRequest?.files) && editingRequest.files.length > 0;
    if (!editingRequest && selectedFiles.length === 0) {
      setNotice({
        open: true,
        severity: 'warning',
        message: 'Adjunta al menos una factura o evidencia antes de crear el ticket.'
      });
      return;
    }
    if (editingRequest && !hasExistingFiles && selectedFiles.length === 0) {
      setNotice({
        open: true,
        severity: 'warning',
        message: 'This ticket requires at least one supporting attachment.'
      });
      return;
    }

    try {
      setSaving(true);
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        payload.append(key, value ?? '');
      });
      selectedFiles.forEach((file) => payload.append('evidence_files', file));

      if (editingRequest) {
        await expenseRequestService.updateRequest(editingRequest.id, payload);
      } else {
        await expenseRequestService.createRequest(payload);
      }

      setNotice({
        open: true,
        severity: 'success',
        message: editingRequest ? 'Expense ticket updated.' : 'Expense ticket created.'
      });
      closeDialog();
      await loadRequests();
    } catch (error) {
      setNotice({
        open: true,
        severity: 'error',
        message: error.response?.data?.message || 'Could not save the ticket.'
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteRequest = async (request) => {
    const confirmDelete = window.confirm(`Delete the ticket "${request.title}"?`);
    if (!confirmDelete) return;

    try {
      await expenseRequestService.deleteRequest(request.id);
      setNotice({
        open: true,
        severity: 'success',
        message: 'Expense ticket deleted.'
      });
      await loadRequests();
    } catch (error) {
      setNotice({
        open: true,
        severity: 'error',
        message: error.response?.data?.message || 'Could not delete the ticket.'
      });
    }
  };

  const openFile = async (requestId, file, download = false) => {
    const actionKey = `${requestId}-${file.id}-${download ? 'download' : 'view'}`;
    if (openingFileKey === actionKey) return;

    try {
      setOpeningFileKey(actionKey);
      const response = await expenseRequestService.getRequestFile(requestId, file.id, { download });
      await openBlobResponse(response, {
        download,
        fallbackName: file.original_name || `evidence-${file.id}`
      });
    } catch (error) {
      setNotice({
        open: true,
        severity: 'error',
        message: error.response?.data?.message || 'Could not open the evidence file.'
      });
    } finally {
      window.setTimeout(() => setOpeningFileKey(''), 600);
    }
  };

  const handleAddComment = async () => {
    if (!editingRequest?.id || !commentDraft.trim()) return;

    try {
      setCommentSubmitting(true);
      await expenseRequestService.addComment(editingRequest.id, commentDraft.trim());
      setCommentDraft('');
      await Promise.all([
        loadComments(editingRequest.id),
        loadRequests()
      ]);
      setNotice({
        open: true,
        severity: 'success',
        message: 'Note added to the conversation.'
      });
    } catch (error) {
      setNotice({
        open: true,
        severity: 'error',
        message: error.response?.data?.message || 'Could not post the note.'
      });
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!editingRequest?.id || !commentId) return;
    const confirmed = window.confirm('Delete this note?');
    if (!confirmed) return;

    try {
      await expenseRequestService.deleteComment(editingRequest.id, commentId);
      await Promise.all([
        loadComments(editingRequest.id),
        loadRequests()
      ]);
      setNotice({
        open: true,
        severity: 'success',
        message: 'Note deleted.'
      });
    } catch (error) {
      setNotice({
        open: true,
        severity: 'error',
        message: error.response?.data?.message || 'Could not delete the note.'
      });
    }
  };

  const kpiCards = useMemo(() => ([
    {
      key: 'total',
      label: canApprove ? 'Visible Tickets' : 'My Tickets',
      value: summary.total || 0,
      icon: ReceiptLongIcon,
      color: theme.palette.primary.main
    },
    {
      key: 'under_review',
      label: 'Under Review',
      value: (summary.under_review || 0) + (summary.submitted || 0),
      icon: WarningAmberIcon,
      color: '#f59e0b'
    },
    {
      key: 'approved',
      label: 'Approved',
      value: summary.approved || 0,
      icon: WorkspacePremiumIcon,
      color: '#38bdf8'
    },
    {
      key: 'paid',
      label: 'Paid',
      value: summary.paid || 0,
      icon: TaskAltIcon,
      color: '#22c55e'
    }
  ]), [canApprove, summary, theme.palette.primary.main]);

  return (
    <Box sx={{ p: 0 }}>
      <Paper
        sx={{
          p: { xs: 1.4, md: 1.7 },
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: alpha(accent, 0.22),
          backgroundImage: `linear-gradient(120deg, ${alpha(accent, 0.18)} 0%, ${alpha('#0f172a', 0.08)} 100%)`
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
              Expense Tickets
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 780, mt: 0.5 }}>
              Each employee records the expense here, attaches an invoice or supporting evidence, and leaves a clear audit trail before requesting payment or reimbursement.
            </Typography>
            <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap sx={{ mt: .7 }}>
              <Chip size="small" label="Invoice required for payment" />
              <Chip size="small" label={`${requests.length} tickets loaded`} />
              <Chip size="small" label={`User: ${user?.first_name || user?.username || user?.email || 'Current user'}`} />
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {hasPermission('expenseRequests', 'create') && <Button variant="contained" startIcon={<ReceiptLongIcon />} onClick={() => navigate('/expense-tickets/quick')} sx={{ minHeight: 44 }}>Gasto rápido</Button>}
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={loadRequests}>
              Refresh
            </Button>
            <Button size="small" variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={openCreateDialog}>
              New Ticket
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Alert severity="info" sx={{ mt: 2, borderRadius: 3 }}>
        Workflow rules: the employee uploads the invoice or supporting evidence, the manager reviews the ticket, and only approved tickets with verifiable support should proceed to payment.
      </Alert>

      <Grid container spacing={1.2} sx={{ mt: 0.25 }}>
        {kpiCards.map((card) => {
          const Icon = card.icon;
          return (
            <Grid item xs={12} sm={6} lg={3} key={card.key}>
              <Paper
                sx={{
                  p: 1.35,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: alpha(card.color, 0.26),
                  bgcolor: alpha(card.color, 0.08)
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {card.label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: card.color }}>
                      {card.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: alpha(card.color, 0.14),
                      color: card.color
                    }}
                  >
                    <Icon />
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ mt: 1.5, p: 1.4, borderRadius: 2.5, border: '1px solid', borderColor: alpha(accent, 0.18) }}>
        <Grid container spacing={1.2}>
          {canApprove && (
            <Grid item xs={12} md={2.4}>
              <TextField
                select
                fullWidth
                label="Scope"
                size="small"
                value={filters.scope}
                onChange={(event) => setFilters((prev) => ({ ...prev, scope: event.target.value }))}
              >
                <MenuItem value="all">All tickets</MenuItem>
                <MenuItem value="mine">Only mine</MenuItem>
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} md={canApprove ? 2.4 : 3}>
            <TextField
              select
              fullWidth
              label="Status"
              size="small"
              value={filters.status}
              onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={canApprove ? 7.2 : 9}>
            <TextField
              fullWidth
              size="small"
              label="Search concept, vendor, invoice, or project..."
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ mt: 1.5, borderRadius: 2.5, overflow: 'hidden', border: '1px solid', borderColor: alpha(accent, 0.16) }}>
        {loading ? (
          <Box sx={{ py: 7, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
          <Stack spacing={1.2} sx={{ display: { xs: 'flex', md: 'none' }, p: 1.2 }}>
            {requests.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
                No expense tickets found with the current filters.
              </Typography>
            ) : requests.map((request) => {
              const chip = getStatusChipProps(request.status);
              const firstFile = Array.isArray(request.files) && request.files.length ? request.files[0] : null;
              const rowCanDelete = hasPermission('expenseRequests', 'delete')
                && (canApprove || !['approved', 'paid'].includes(String(request.status || '').toLowerCase()));
              return (
                <Paper key={`mobile-ticket-${request.id}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2.2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={850} sx={{ overflowWrap: 'anywhere' }}>{request.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{request.category} • {formatDate(request.expense_date)}</Typography>
                    </Box>
                    <Typography variant="subtitle2" fontWeight={900} color="primary.main" sx={{ flexShrink: 0 }}>{formatMoney(request.amount, request.currency)}</Typography>
                  </Stack>
                  <Box sx={{ mt: 1.2, display: 'grid', gridTemplateColumns: 'minmax(90px, .7fr) minmax(0, 1.3fr)', gap: 0.7 }}>
                    <Typography variant="caption" color="text.secondary">Requester</Typography><Typography variant="body2" sx={{ textAlign: 'right', overflowWrap: 'anywhere' }}>{request.requester_name || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">Project</Typography><Typography variant="body2" sx={{ textAlign: 'right', overflowWrap: 'anywhere' }}>{request.project_name || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">Vendor</Typography><Typography variant="body2" sx={{ textAlign: 'right', overflowWrap: 'anywhere' }}>{request.vendor_name || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">Invoice</Typography><Typography variant="body2" sx={{ textAlign: 'right', overflowWrap: 'anywhere' }}>{request.invoice_number || 'No invoice number'}</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.3 }}>
                    <Chip size="small" label={chip.label} sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 700, border: '1px solid', borderColor: alpha(chip.color, 0.35) }} />
                    <Chip size="small" icon={<AttachFileIcon />} label={`${request.attachment_count || 0} file(s)`} variant="outlined" />
                    {firstFile && <IconButton size="small" onClick={() => openFile(request.id, firstFile, false)}><VisibilityIcon fontSize="small" /></IconButton>}
                    {firstFile && <IconButton size="small" onClick={() => openFile(request.id, firstFile, true)}><DownloadIcon fontSize="small" /></IconButton>}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.3 }}>
                    <Button fullWidth size="small" variant="outlined" onClick={() => openEditDialog(request)}>Open</Button>
                    {rowCanDelete && <IconButton size="small" color="error" onClick={() => deleteRequest(request)}><DeleteOutlineIcon fontSize="small" /></IconButton>}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
          <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Requester</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Vendor / Invoice</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Evidence</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        No expense tickets found with the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : requests.map((request) => {
                  const chip = getStatusChipProps(request.status);
                  const firstFile = Array.isArray(request.files) && request.files.length ? request.files[0] : null;
                  const rowCanDelete = hasPermission('expenseRequests', 'delete')
                    && (canApprove || !['approved', 'paid'].includes(String(request.status || '').toLowerCase()));
                  return (
                    <TableRow key={request.id} hover>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                          {request.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.category} • {formatDate(request.expense_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>{request.requester_name || '-'}</TableCell>
                      <TableCell>{request.project_name || '-'}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="body2">{request.vendor_name || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.invoice_number || 'No invoice number'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatMoney(request.amount, request.currency)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={chip.label}
                          sx={{
                            bgcolor: chip.bg,
                            color: chip.color,
                            fontWeight: 700,
                            border: '1px solid',
                            borderColor: alpha(chip.color, 0.35)
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            icon={<AttachFileIcon />}
                            label={`${request.attachment_count || 0} file(s)`}
                            variant="outlined"
                          />
                          {firstFile && (
                            <IconButton size="small" onClick={() => openFile(request.id, firstFile, false)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          )}
                          {firstFile && (
                            <IconButton size="small" onClick={() => openFile(request.id, firstFile, true)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{formatDate(request.updated_at)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button size="small" onClick={() => openEditDialog(request)}>
                            Open
                          </Button>
                          {rowCanDelete && (
                            <IconButton size="small" color="error" onClick={() => deleteRequest(request)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}
      </Paper>

      <ExpenseTicketDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSubmit={submitForm}
        saving={saving}
        form={form}
        setForm={setForm}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        projects={projects}
        canApprove={canApprove}
        editMode={Boolean(editingRequest)}
        existingFiles={editingRequest?.files || []}
        onOpenExistingFile={openFile}
        requestId={editingRequest?.id}
        comments={comments}
        commentsLoading={commentsLoading}
        commentDraft={commentDraft}
        onCommentDraftChange={setCommentDraft}
        onAddComment={handleAddComment}
        onDeleteComment={handleDeleteComment}
        commentSubmitting={commentSubmitting}
        currentUserId={user?.id}
      />

      <Snackbar
        open={notice.open}
        autoHideDuration={3600}
        onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotice((prev) => ({ ...prev, open: false }))} severity={notice.severity} variant="filled">
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
